@if (!isEditor) {
  <div class="page-header">
    <div>
      <h1>{{ t('DonationFormPageLink') }}</h1>
      <p>{{ t('HubFormsIntro') }}</p>
    </div>
    <button class="primary-button" type="button" (click)="createModalOpen = true">{{ t('AddFormButton') }}</button>
  </div>
  @if (error) {
    <p class="message error">{{ error }}</p>
  }
  @if (success) {
    <p class="message success">{{ success }}</p>
  }
  @if (loading) {
    <p>{{ t('HubFormsLoading') }}</p>
  }
  @if (!loading) {
    <div class="card">
      <table class="table">
        <thead>
          <tr>
            <th></th>
            <th>{{ t('FormName') }}</th>
            <th>{{ t('FormCampaign') }}</th>
            <th>{{ t('HubActions') }}</th>
          </tr>
        </thead>
        <tbody>
          @for (form of forms; track form) {
            <tr>
              <td data-label=""><span class="color-dot" [style.background-color]="form.color"></span></td>
              <td [attr.data-label]="t('FormName')"><a class="row-link" [routerLink]="['/forms', form.id]">{{ form.name }}</a></td>
              <td [attr.data-label]="t('FormCampaign')">{{ form.campaign }}</td>
              <td [attr.data-label]="t('HubActions')">
                <a class="secondary-button action-link" [routerLink]="['/forms', form.id]">{{ t('HubOpenEditor') }}</a>
                <button class="danger-button" type="button" (click)="deleteForm(form)">{{ t('DeleteFormButton') }}</button>
              </td>
            </tr>
          }
          @if (forms.length === 0) {
            <tr>
              <td colspan="4">{{ t('HubNoForms') }}</td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  }
  @if (createModalOpen) {
    <div class="modal-backdrop" (click)="createModalOpen = false">
      <form class="modal-card" (click)="$event.stopPropagation()" (ngSubmit)="createForm()">
        <h2>{{ t('AddFormButton') }}</h2>
        <label>{{ t('FormNameLabel') }}<input name="newFormName" [(ngModel)]="newFormName" required></label>
        <div class="button-row">
          <button class="secondary-button" type="button" (click)="createModalOpen = false">{{ t('DeleteFormCancelButtonLabel') }}</button>
          <button class="primary-button" type="submit">{{ t('HubCreate') }}</button>
        </div>
      </form>
    </div>
  }
}

@if (isEditor) {
  @if (editor) {
    <div class="page-header editor-header">
      <div>
        <a routerLink="/forms" class="back-link">{{ t('DonationFormPageLink') }}</a>
        <h1>{{ editor.form.name || t('FormNameLabel') }}</h1>
        <p>{{ t('HubFormEditorIntro') }}</p>
      </div>
      <div class="editor-status" [class.dirty]="saveState !== 'saved'">
        {{ saveStatusLabel() }}
        @if (validationCount()) {
          <span class="validation-pill">{{ validationCount() }} {{ t('HubFormValidationIssues') }}</span>
        }
      </div>
    </div>
  }
  @if (error) {
    <p class="message error">{{ error }}</p>
  }
  @if (success) {
    <p class="message success">{{ success }}</p>
  }
  @if (loading) {
    <p>{{ t('HubFormEditorLoading') }}</p>
  }
  @if (!loading && editor) {
    <div class="form-editor-layout">
      <section class="card editor-card">
        <select class="mobile-tab-select section-select" [ngModel]="activeSection" (ngModelChange)="setSection($event)" aria-label="Form editor section">
          @for (section of sectionItems; track section) {
            <option [value]="section.value">{{ t(section.labelKey) }}</option>
          }
        </select>
        <div class="accordion-section" [class.open]="activeSection === 'properties'" [class.invalid]="sectionInvalid('properties')">
          <button type="button" class="accordion-header" (click)="setSection('properties')" [attr.aria-expanded]="activeSection === 'properties'" aria-controls="form-properties-panel">
            <span class="accordion-caret"></span>
            <span>{{ t('FormProperties') }}</span>
            @if (validationCount('properties')) {
              <span class="accordion-badge">{{ validationCount('properties') }}</span>
            }
          </button>
          @if (activeSection === 'properties') {
            <div id="form-properties-panel" class="accordion-panel form-grid">
              <label>{{ t('FormNameLabel') }}
                <input name="formName" [(ngModel)]="editor.form.name" (ngModelChange)="saveTextField()">
              </label>
              <label>{{ t('FormPayrexxIntegrationTypeLabel') }}
                <select name="payrexxType" [(ngModel)]="editor.form.payrexxIntegrationType" (ngModelChange)="markDirty()">
                  <option value="forward">{{ t('FormPayrexxIntegrationTypeForwardLabel') }}</option>
                  <option value="popup">{{ t('FormPayrexxIntegrationTypePopupLabel') }}</option>
                </select>
              </label>
              <label>{{ t('FormSuccessRedirectUrlLabel') }}
                <input type="url" name="successRedirectUrl" [(ngModel)]="editor.form.successRedirectUrl" (ngModelChange)="saveTextField()" placeholder="https://example.com/success.html">
                @if (validationFor('properties.successRedirectUrl')) {
                  <span class="field-error">{{ validationFor('properties.successRedirectUrl') }}</span>
                }
              </label>
              <label>{{ t('FormFailedRedirectUrlLabel') }}
                <input type="url" name="failedRedirectUrl" [(ngModel)]="editor.form.failedRedirectUrl" (ngModelChange)="saveTextField()" placeholder="https://example.com/failure.html">
                @if (validationFor('properties.failedRedirectUrl')) {
                  <span class="field-error">{{ validationFor('properties.failedRedirectUrl') }}</span>
                }
              </label>
              <label>{{ t('FormCancelRedirectUrlLabel') }}
                <input type="url" name="cancelRedirectUrl" [(ngModel)]="editor.form.cancelRedirectUrl" (ngModelChange)="saveTextField()" placeholder="https://example.com/cancel.html">
                @if (validationFor('properties.cancelRedirectUrl')) {
                  <span class="field-error">{{ validationFor('properties.cancelRedirectUrl') }}</span>
                }
              </label>
            </div>
          }
        </div>
        <div class="accordion-section" [class.open]="activeSection === 'style'">
          <button type="button" class="accordion-header" (click)="setSection('style')" [attr.aria-expanded]="activeSection === 'style'" aria-controls="form-style-panel">
            <span class="accordion-caret"></span>
            <span>{{ t('FormStyle') }}</span>
          </button>
          @if (activeSection === 'style') {
            <div id="form-style-panel" class="accordion-panel form-grid two-columns">
              <label>{{ t('FormColorLabel') }}
                <input type="color" name="formColor" [(ngModel)]="editor.form.color" (ngModelChange)="markDirty()">
              </label>
              <label>{{ t('FormBackgroundColorLabel') }}
                <input type="color" name="backgroundColor" [(ngModel)]="editor.form.backgroundColor" (ngModelChange)="markDirty()">
              </label>
              <label>{{ t('FormButtonColorLabel') }}
                <input type="color" name="buttonColor" [(ngModel)]="editor.form.buttonColor" (ngModelChange)="markDirty()">
              </label>
              <label>{{ t('FormBorderRadius') }}
                <input type="number" name="borderRadius" min="0" max="20" [(ngModel)]="editor.form.borderRadius" (ngModelChange)="markDirty()">
              </label>
            </div>
          }
        </div>
        <div class="accordion-section" [class.open]="activeSection === 'amounts'" [class.invalid]="sectionInvalid('amounts')">
          <button type="button" class="accordion-header" (click)="setSection('amounts')" [attr.aria-expanded]="activeSection === 'amounts'" aria-controls="form-amounts-panel">
            <span class="accordion-caret"></span>
            <span>{{ t('FormAmounts') }}</span>
            @if (validationCount('amounts')) {
              <span class="accordion-badge">{{ validationCount('amounts') }}</span>
            }
          </button>
          @if (activeSection === 'amounts') {
            <div id="form-amounts-panel" class="accordion-panel stacked-section">
              <label class="compact-field">{{ t('FormCurrencyLabel') }}
                <select name="currency" [(ngModel)]="editor.form.currency" (ngModelChange)="markDirty()">
                  @for (currency of editor.currencies; track currency) {
                    <option [value]="currency">{{ currency }}</option>
                  }
                </select>
              </label>
              <div class="tab-list nested-tabs" role="tablist">
                @for (frequency of frequencyItems; track frequency) {
                  <button type="button" class="tab-button" [class.active]="activeFrequency === frequency.value" [class.invalid]="sectionInvalid(frequency.value)" (click)="setFrequency(frequency.value)">{{ t(frequency.labelKey) }}</button>
                }
              </div>
              <select class="mobile-tab-select" [ngModel]="activeFrequency" (ngModelChange)="setFrequency($event)" aria-label="Amount frequency">
                @for (frequency of frequencyItems; track frequency) {
                  <option [value]="frequency.value">{{ t(frequency.labelKey) }}</option>
                }
              </select>
              <div class="amount-list">
                @for (amount of amounts(activeFrequency); track amount; let i = $index) {
                  <div class="amount-row">
                    <label>{{ i + 1 }}. {{ t('FormAmountLabel') }}
                      <input type="number" min="1" [(ngModel)]="amount.amount" [name]="'amount-' + amount.id" (ngModelChange)="markDirty()">
                    </label>
                    <button class="secondary-button" type="button" (click)="deleteAmount(amount)">{{ t('RemoveAmount') }}</button>
                    @if (validationFor('formAmount-' + amount.id)) {
                      <span class="field-error">{{ validationFor('formAmount-' + amount.id) }}</span>
                    }
                  </div>
                }
                @if (activeFrequency === 'once' && validationFor('amounts.once.allOnceAmountsError')) {
                  <p class="field-error">{{ validationFor('amounts.once.allOnceAmountsError') }}</p>
                }
                @if (activeFrequency === 'monthly' && validationFor('amounts.monthly.allMonthlyAmountsError')) {
                  <p class="field-error">{{ validationFor('amounts.monthly.allMonthlyAmountsError') }}</p>
                }
                <div class="inline-form add-row">
                  @if (activeFrequency === 'once') {
                    <input type="number" min="1" name="newOnceAmount" [(ngModel)]="newOnceAmount" (keyup.enter)="addAmount('once')" [placeholder]="t('NewOnceAmountValue')">
                  }
                  @if (activeFrequency === 'monthly') {
                    <input type="number" min="1" name="newMonthlyAmount" [(ngModel)]="newMonthlyAmount" (keyup.enter)="addAmount('monthly')" [placeholder]="t('NewMonthlyAmountValue')">
                  }
                  <button class="primary-button" type="button" (click)="addAmount(activeFrequency)">{{ t('AddAmount') }}</button>
                </div>
              </div>
            </div>
          }
        </div>
        <div class="accordion-section" [class.open]="activeSection === 'designations'" [class.invalid]="sectionInvalid('designations')">
          <button type="button" class="accordion-header" (click)="setSection('designations')" [attr.aria-expanded]="activeSection === 'designations'" aria-controls="form-designations-panel">
            <span class="accordion-caret"></span>
            <span>{{ t('FormDesignations') }}</span>
            @if (validationCount('designations')) {
              <span class="accordion-badge">{{ validationCount('designations') }}</span>
            }
          </button>
          @if (activeSection === 'designations') {
            <div id="form-designations-panel" class="accordion-panel stacked-section">
              <label>{{ t('FormCampaignLabel') }}
                <input name="campaign" [(ngModel)]="editor.form.campaign" (ngModelChange)="saveTextField()">
              </label>
              <label class="checkbox-label">
                <input type="checkbox" name="designationsUsePictures" [(ngModel)]="editor.form.designationsUsePictures" (ngModelChange)="markDirty()">
                <span>{{ t('FormDesignationsUsePicturesLabel') }}</span>
              </label>
              <h2>{{ t('FormDesignationsHeaderLabel') }}</h2>
              @for (designation of editor.designations; track designation; let i = $index) {
                <div class="designation-row">
                  <label>{{ i + 1 }}. {{ t('FormDesignationLabel') }}
                    <input [(ngModel)]="designation.designation" [name]="'designation-' + designation.id" (ngModelChange)="saveTextField()">
                    @if (validationFor('formDesignation-' + designation.id)) {
                      <span class="field-error">{{ validationFor('formDesignation-' + designation.id) }}</span>
                    }
                  </label>
                  @if (editor.form.designationsUsePictures) {
                    <div class="designation-picture">
                      @if (designation.hasPicture) {
                        <img [src]="designation.imageUrl" [alt]="designation.designation">
                      }
                      <label class="secondary-button file-button">
                        {{ designation.hasPicture ? t('FormReplaceDesignationImage') : t('FormUploadDesignationImage') }}
                        <input type="file" accept="image/png, image/jpeg" (change)="uploadDesignationPicture(designation, $event)">
                      </label>
                      @if (validationFor('formDesignationPicture-' + designation.id)) {
                        <span class="field-error">{{ validationFor('formDesignationPicture-' + designation.id) }}</span>
                      }
                    </div>
                  }
                  <button class="secondary-button" type="button" (click)="deleteDesignation(designation)">{{ t('RemoveDesignationButtonLabel') }}</button>
                </div>
              }
              <div class="inline-form add-row">
                <input name="newDesignationName" [(ngModel)]="newDesignationName" (keyup.enter)="addDesignation()" [placeholder]="t('NewDesignationNameLabel')">
                <button class="primary-button" type="button" (click)="addDesignation()">{{ t('AddDesignationButtonLabel') }}</button>
              </div>
            </div>
          }
        </div>
        <div class="accordion-section" [class.open]="activeSection === 'translations'" [class.invalid]="sectionInvalid('translations')">
          <button type="button" class="accordion-header" (click)="setSection('translations')" [attr.aria-expanded]="activeSection === 'translations'" aria-controls="form-translations-panel">
            <span class="accordion-caret"></span>
            <span>{{ t('FormTranslations') }}</span>
            @if (validationCount('translations')) {
              <span class="accordion-badge">{{ validationCount('translations') }}</span>
            }
          </button>
          @if (activeSection === 'translations') {
            <div id="form-translations-panel" class="accordion-panel stacked-section">
              <div class="tab-list nested-tabs" role="tablist">
                @for (language of editor.languages; track language) {
                  <button type="button" class="tab-button" [class.active]="language === activeLanguage" [class.invalid]="sectionInvalid(language)" (click)="setLanguage(language)">{{ language }}</button>
                }
              </div>
              <select class="mobile-tab-select" [ngModel]="activeLanguage" (ngModelChange)="setLanguage($event)" aria-label="Translation language">
                @for (language of editor.languages; track language) {
                  <option [value]="language">{{ language }}</option>
                }
              </select>
              <div class="translation-grid">
                @for (label of editor.labels; track label) {
                  <label>
                    {{ label }}
                    <input [name]="'translation-' + activeLanguage + '-' + label" [(ngModel)]="editor.translations[label][activeLanguage]" [placeholder]="label" (ngModelChange)="saveTextField()">
                    @if (validationFor('translationLabel-' + activeLanguage + '-' + label)) {
                      <span class="field-error">{{ validationFor('translationLabel-' + activeLanguage + '-' + label) }}</span>
                    }
                  </label>
                }
              </div>
            </div>
          }
        </div>
        <div class="accordion-section" [class.open]="activeSection === 'embed'">
          <button type="button" class="accordion-header" (click)="setSection('embed')" [attr.aria-expanded]="activeSection === 'embed'" aria-controls="form-embed-panel">
            <span class="accordion-caret"></span>
            <span>{{ t('InsertIntoWebpage') }}</span>
          </button>
          @if (activeSection === 'embed') {
            <div id="form-embed-panel" class="accordion-panel stacked-section">
              <p>{{ t('WidgetInstructions') }}</p>
              <div class="embed-code-row">
                <input class="embed-code" readonly [value]="editor.embedCode">
                <button class="secondary-button" type="button" (click)="copyText(editor.embedCode)">{{ t('HubCopyEmbedCode') }}</button>
              </div>
            </div>
          }
        </div>
        <div class="accordion-section" [class.open]="activeSection === 'twint'">
          <button type="button" class="accordion-header" (click)="setSection('twint')" [attr.aria-expanded]="activeSection === 'twint'" aria-controls="form-twint-panel">
            <span class="accordion-caret"></span>
            <span>{{ t('twint') }}</span>
          </button>
          @if (activeSection === 'twint') {
            <div id="form-twint-panel" class="accordion-panel stacked-section">
              @if (editor.twint.active) {
                @if (!editor.twint.qrAvailable) {
                  <p class="message error">{{ t('HubTwintQrMissing') }}</p>
                }
                @if (editor.twint.qrAvailable) {
                  <img class="qr-code" [src]="editor.twint.qrCodeUrl" alt="QR Code">
                }
                @if (editor.twint.qrAvailable) {
                  <a [href]="editor.twint.qrCodeUrl" download>{{ t('downloadTwintQrCode') }}</a>
                }
                @if (editor.twint.embedCode) {
                  <div class="embed-code-row">
                    <input class="embed-code" readonly [value]="editor.twint.embedCode">
                    <button class="secondary-button" type="button" (click)="copyText(editor.twint.embedCode)">{{ t('HubCopyEmbedCode') }}</button>
                  </div>
                }
                <label>{{ t('twintCustomCss') }}
                  <textarea name="customCss" rows="8" [(ngModel)]="editor.form.customCss" (ngModelChange)="saveTextField()"></textarea>
                </label>
              } @else {
                <p>{{ t('twintNotAvailable') }}</p>
              }
            </div>
          }
        </div>
      </section>
      <section class="preview-column">
        <div class="card preview-card">
          <div class="preview-header">
            <h2>{{ t('LoginIncludePreviewHeader') }}</h2>
            <div class="tab-list compact-tabs" role="tablist">
              @for (language of editor.languages; track language) {
                <button type="button" class="tab-button" [class.active]="language === previewLanguage" (click)="setPreviewLanguage(language)">{{ language }}</button>
              }
            </div>
            <select class="mobile-tab-select" [ngModel]="previewLanguage" (ngModelChange)="setPreviewLanguage($event)" aria-label="Preview language">
              @for (language of editor.languages; track language) {
                <option [value]="language">{{ language }}</option>
              }
            </select>
          </div>
          <iframe [src]="previewUrl(previewLanguage)" title="Donation form preview"></iframe>
        </div>
      </section>
    </div>
  }
}
